import {
  Button,
  ButtonGroup,
  Code,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React from 'react';

import {
  graphPersistedQueriesRouteConfig,
  variantPersistedQueriesRouteConfig,
} from 'src/app/graph/routes';
import { Form as FormWrapper } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';

import { PersistedQueryListConfigHeader } from '../PersistedQueryListConfigHeader';
import {
  PersistedQueryListConfigStep,
  PersistedQueryListConfigWorkflow,
} from '../steps';

import { PersistedQueryListPublishInfo } from './PersistedQueryListPublishInfo';

export interface PersistedQueryListPublishModalState {
  graphId?: string;
  listId?: string;
  linkedVariantId?: string;
}

interface Props {
  isOpen: boolean;
  state: PersistedQueryListPublishModalState | null;
  onComplete: () => void;
  onPrevious: () => void;
  onClose: () => void;
}

export const PersistedQueryListPublishModal = ({
  isOpen,
  state,
  onComplete,
  onPrevious,
  onClose,
}: Props) => {
  const { graphId: graphIdFromUrl } = useRouteParams(
    graphPersistedQueriesRouteConfig,
    variantPersistedQueriesRouteConfig,
  );

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <FormWrapper onSubmit={onComplete} noValidate>
        <ModalContent>
          <ModalHeader>
            <PersistedQueryListConfigHeader
              currentStep={PersistedQueryListConfigStep.Publish}
              workflow={PersistedQueryListConfigWorkflow.create}
            />
            <ModalTitle>Optional: Publish Operations</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p>
              Your persisted query list has been created with the following ID:
              <Code canCopy>{state?.listId ?? '<unknown list>'}</Code>
            </p>
            {!state?.listId /* || errors */ ? (
              <ErrorMessage>
                Something went wrong. Please refresh and try again.
              </ErrorMessage>
            ) : (
              <section>
                <PersistedQueryListPublishInfo
                  graphId={graphIdFromUrl}
                  listId={state.listId}
                  linkedVariantId={state?.linkedVariantId}
                />
              </section>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <ButtonGroup>
              <Button variant="secondary" type="button" onClick={onPrevious}>
                Previous
              </Button>
              <Button variant="primary" type="submit">
                Continue
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
